/**
	* Miscellaneous tools
	* Handles: random stuff
	* @module tools/misc
*/

import $ from "jquery";

/**
	* Get window dimensions
	* 
	* @remarks
	* Returns an object like `{width: 800, height: 300}`
*/
export function getWindowDimensions() {
	return {
		width: $("#opusmap").width(),
		height: $("#opusmap").height()
	}
}

/**
	* Update URL parameters
	* 
	* @param url		-	URL to modify
	* @param param		-	Parameter name
	* @param paramVal	-	Parameter new value
	* 
	* @remarks
	* Modifies a url's parameters to new values
*/
export function updateURLParameter(url:string, param:string, paramVal:string){
	let newAdditionalURL = "";
	let tempArray = url.split("?");
	let baseURL = tempArray[0];
	let additionalURL = tempArray[1];
	let temp = "";
	
	if (additionalURL) {
		tempArray = additionalURL.split("&");
		
		for (let i=0; i<tempArray.length; i++){
			if(tempArray[i].split('=')[0] != param){
				newAdditionalURL += temp + tempArray[i];
				temp = "&";
			}
		}
	}
	
	let rows_txt = temp + "" + param + "=" + paramVal;
	return baseURL + "?" + newAdditionalURL + rows_txt;
}

/**
	* Update URL parameters
	* 
	* @param {string} url - URL to modify
	* @param {any} obj - Keys and values for the param names/values
*/
export function updateURLParams(url:string, obj:any) {
	for(let key in obj) {
		url = updateURLParameter(url, key, obj[key]);
	}
	
	return url;
}

/**
	* Make a url based on base url and parameters
	* 
	* @param {string} url - Base url
	* @param {Map<string, any>} params - url params
*/
export function makeUrl(url:string, params:Map<string, any>) {
	params.forEach((value:any, key:string) => {
		url = updateURLParameter(url, key, value);
	});
	return encodeURI(url);
}

/**
	* Clone object
	* 
	* @param orig	-	Object to clone
*/
export function clone(orig:any) {
	return Object.assign(Object.create(Object.getPrototypeOf(orig)), orig);
}

/**
	* Limit string length
	* 
	* @param {string} str - String to limit
	* @param {number} maxLength - Maximum length
*/
export function limitString(str:string, maxLength:number):string {
	if(!str) return "";
	if(str.length < maxLength) return str;
	
	return str.substr(0, maxLength-3) + "...";
}

/**
	* Get timestamp
	
	* @returns
	* Unix timestamp
*/
export function time():number {
	return new Date().getTime() / 1000;
}

/**
	* Get offset bottom css with jquery
	* @returns
	* {number} offset bottom
*/
export function getOffsetBottomPixels(element:string):number {
	const link = $(element);
	const position = link.position();
	const bottom:number = $("#opusmap").height() - position?.top - link.height();
	
	return bottom;
}

/**
	* Get offset right css with jquery
	* @returns
	* {number} offset right
*/
export function getOffsetRightPixels(element:string):number {
	const link = $(element);
	const position = link.position();
	const right:number = $("#opusmap").width() - position?.left - link.width();
	
	return right;
}

/**
	* Turn a string into a safe id
	* @param {string} str
	* 
	* @returns {string} idified string
*/
export function idify(str:string) {
	return str.replace(" ", "").replace("'", "").replace("\"", "").replace(".", "");
}

/**
	* Turn a string into a slug
	* @param {string} str
	* 
	* @returns {string} slugified string
*/
export function slugify(str:string) {
	return str.toLowerCase().replace(/ /g, "-").replace(/[^\w-]+/g, "");
}

/**
	* Print date string
	* @returns {string} date
*/
export function dateString() {
	const options:any = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
	
	return new Date().toLocaleDateString("en-US", options);
}

/**
	* Print date & time string
	* @returns {string} date & time
*/
export function dateTimeString() {
	const options:any = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
	
	return new Date().toLocaleDateString("en-US", options);
}

/**
	* A foreach method that gives manual control over when the next iteration will occur
	* 
	* @param {any[]} array - Array to iterate
	* @param {Function} method - Handler
	* @param {Function} onFinish - Method to call when iteration completes
*/
export function forEachManual(array:any[], method:Function, onFinish:Function) {
	if(array.length < 1) {
		onFinish();
		return;
	}
		
	let item:any = array.shift();
	let nextCall:Function = () => {
		forEachManual(array, method, onFinish);
	}
	
	const res:any = method(item, nextCall);
	
	if(res === true)
		nextCall();
}

/**
	* Read blob and return data url
	* 
	* @param {File} blob - Blob to read
*/
export function blobToDataURL(blob:File) {
	return new Promise((resolve, reject) => {
		const reader:FileReader = new FileReader();
		
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
		
		reader.readAsDataURL(blob);
	});
}

/**
	* Remove element from standard array
	* 
	* @param {any} element
	* @param {any[]} array
	* 
	* @returns {bool} success
*/
export function removeFromArray(element:any, elements:any[]):boolean {
	const index:number = elements.indexOf(element);
	
	if(index < 0)
		return false;
	
	elements.splice(index, 1);
	
	return true;
}

/**
	* Add element to end of standard array without allowing repeat
	* 
	* @param {any} element
	* @param {any[]} array
	* 
	* @returns {bool} success
*/
export function addToArrayUnique(element:any, elements:any[]):boolean {
	const index:number = elements.indexOf(element);
	
	if(index > -1)
		return false;
	
	elements.push(element);
	
	return true;
}

/**
	* Add element to start of standard array without allowing repeat
	* 
	* @param {any} element
	* @param {any[]} array
	* 
	* @returns {bool} success
*/
export function unshiftToArrayUnique(element:any, elements:any[]):boolean {
	const index:number = elements.indexOf(element);
	
	if(index > -1)
		return false;
	
	elements.unshift(element);
	
	return true;
}

/**
	* Returns the key of the first occurrence of a given value in the map.
	* @param {Map} map - The Map object to search in.
	* @param {any} targetValue - The value to search for.
	* @returns {any} - The key corresponding to the targetValue, or -1 if not found.
*/
export function getIndexByValue(map:Map<any, any>, targetValue:any) {
	let indexResult:any;
	
	map.forEach((value:any, key:any) => {
		if (value === targetValue)
			indexResult = key;
	});
	
	return indexResult;
}

/**
	* Process label text for word wrap
	* 
	* @returns {string} The word wrapped string
*/
export function wordWrapString(text:string):string {
	if(!text)
		return text;
	
	const chunks:string[] = [];
	const breakEvery:number = 30;
	
	let str:string = '';
	let caret:number = 0;
	
	for(let i = 0; i < text.length; i++) {
		const char:string = text.charAt(i);
		
		str += char;
		caret++;
		
		if(caret < breakEvery)
			continue;
		
		if(char != ' ')
			continue;
		
		chunks.push(str);
		
		str = '';
		caret = 0;
	}
	
	if(str.length > 0)
		chunks.push(str);
	
	return chunks.join('\n');
}

/**
	* Match filter method
	* @pararm {string} term - Matching term
	* @pararm {string} filter - Filter to match against
	* 
	* @returns {boolean} matches?
*/
export function matchFilter(str:string, filter:string):boolean {
	if(!str)
		return false;
	
	return str.toLowerCase().includes(filter.toLowerCase());
}

/**
	* Parse URL(s)
	* @param {string} serialised strings
	* 
	* @returns {any[]} unserialized object
 */
export function parseURLs(data:string):any[] {
	if(!data) return null;
	
	const urls:any[] = [];
	const urlBlocks:string[] = data.split('|');
	
	for(let i = 0; i < urlBlocks.length; i++) {
		const urlData:string = urlBlocks[i];
		const parts:string[] = urlData.split('[');
		const url:string = parts[0];
		const label:string = parts[1]?.split(']')?.[0];
		
		if(!url || !label) continue;
		
		const urlFinal:string = url.replace('{language}', _config.lang.getShortId());
		
		urls.push(
			{
				label: label,
				url: urlFinal
			}
		);
	}
	
	return urls;
		
}

export function parseCssVariables(css: string): Record<string, string> {
	const match = css.match(/:root\s*{([^}]*)}/);
	if (!match) return {};
	const body = match[1];
	const vars: Record<string, string> = {};
	body.split(';').forEach(line => {
		const [key, value] = line.split(':').map(s => s?.trim());
		if (key && key.startsWith('--') && value) vars[key] = value;
	});
	return vars;
}